// /oauth/consent — согласие на доступ коннектора (Claude.ai, ChatGPT) к MCP. Открывается после
// входа: сервер присылает сюда из /oauth/authorize с зашифрованным параметром `request`.

import { useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'

import { errorMessage } from '@/api/client'
import { Button, Card, EmptyState, ErrorText, PageSpinner } from '@/components/ui'

import { navigation, useConsentDecision, useConsentInfo } from './api'

export function ConsentPage() {
  const [params] = useSearchParams()
  const request = params.get('request')
  const info = useConsentInfo(request)
  const decision = useConsentDecision()
  const [chosen, setChosen] = useState<boolean | null>(null)

  const frame = (node: ReactNode) => (
    <main className="mx-auto flex min-h-dvh w-full max-w-lg flex-col justify-center px-4 py-10">{node}</main>
  )

  if (!request) {
    return frame(
      <EmptyState
        icon="alert"
        title="Нет запроса на доступ"
        description="Открой эту страницу из коннектора: начни подключение в Claude.ai или ChatGPT заново."
      />,
    )
  }
  if (info.isPending) return frame(<PageSpinner />)
  if (info.isError) {
    return frame(
      <EmptyState icon="alert" title="Ссылка не подходит" description={errorMessage(info.error)} />,
    )
  }

  const decide = (approve: boolean) => {
    setChosen(approve)
    decision.mutate(
      { request, approve },
      { onSuccess: (res) => navigation.go(res.redirect_to) },
    )
  }
  const name = info.data.client_name

  return frame(
    <Card title={`${name} просит доступ на чтение и запись`} subtitle={`Возврат на ${info.data.redirect_host}`}>
      <ul className="grid gap-2 text-sm text-fg">
        <li>Читать: вес и замеры, сон, активности, тренировки, план, цели, журнал здоровья.</li>
        <li>Записывать: вес, давление, самочувствие, питание, анализы, тренировки, план, цели.</li>
        <li className="text-muted">
          Каждая запись попадёт в журнал с пометкой «oauth:{name}» и откатывается одной кнопкой. Отозвать доступ можно в
          Настройках → MCP.
        </li>
      </ul>
      {decision.isError ? <ErrorText error={decision.error} className="mt-4" /> : null}
      <div className="mt-6 flex flex-wrap gap-2">
        <Button
          variant="primary"
          loading={decision.isPending && chosen === true}
          disabled={decision.isPending || decision.isSuccess}
          onClick={() => decide(true)}
        >
          Разрешить
        </Button>
        <Button
          loading={decision.isPending && chosen === false}
          disabled={decision.isPending || decision.isSuccess}
          onClick={() => decide(false)}
        >
          Отказать
        </Button>
      </div>
    </Card>,
  )
}
